// Browser-only identifier, never the Ncloud Client Secret or public-data API key.
export interface MapPoint { id: string; name: string; latitude: number; longitude: number; listNumber: number }
export interface UserMapPosition { latitude: number; longitude: number; accuracyMeters: number }
export const userPositionLabel = (position: UserMapPosition): string => position.accuracyMeters > 1000 ? "대략적인 내 위치" : "내 위치";
interface Position { lat(): number; lng(): number }
interface Bounds { extend(position: Position): void; getSW(): Position; getNE(): Position }
interface MapInstance {
  fitBounds(bounds: Bounds, margin: number): void;
  setCenter(position: Position): void;
  getCenter(): Position;
  getZoom(): number;
  setZoom(zoom: number): void;
  setSize(size: { width: number; height: number }): void;
  destroy(): void;
}
interface Marker { setMap(map: MapInstance | null): void }
export interface NaverSdk {
  LatLng: new (latitude: number, longitude: number) => Position;
  LatLngBounds: new () => Bounds;
  Map: new (element: HTMLElement, options: Record<string, unknown>) => MapInstance;
  Marker: new (options: Record<string, unknown>) => Marker;
  Circle: new (options: Record<string, unknown>) => Marker & { getBounds(): Bounds };
  Point: new (x: number, y: number) => { x: number; y: number };
  Event: { addListener(target: MapInstance, event: string, callback: () => void): object; removeListener(listener: object): void };
}
declare global {
  interface Window {
    naver?: { maps: NaverSdk };
    predocMapReady?: () => void;
    navermap_authFailure?: () => void;
  }
}

let pending: { clientId: string; promise: Promise<NaverSdk> } | undefined;
export const mapAuthFailureEvent = "predoc-map-auth-failure";

export function loadNaverMap(clientId: string): Promise<NaverSdk> {
  if (!/^[a-zA-Z0-9_-]{5,100}$/.test(clientId)) return Promise.reject(new Error("MAP_CONFIGURATION"));
  if (pending?.clientId === clientId) return pending.promise;
  // Changing credentials while a page is open must not reuse an authenticated SDK.
  if (pending !== undefined) return Promise.reject(new Error("MAP_RELOAD_REQUIRED"));
  const promise = new Promise<NaverSdk>((resolve, reject) => {
    const script = document.createElement("script");
    const timer = window.setTimeout(() => fail(), 12_000);
    const fail = (): void => {
      window.clearTimeout(timer);
      pending = undefined;
      script.remove();
      delete window.predocMapReady;
      reject(new Error("MAP_UNAVAILABLE"));
    };
    window.navermap_authFailure = () => {
      window.dispatchEvent(new Event(mapAuthFailureEvent));
      fail();
    };
    // SDK 3.10.1 can invoke its callback before assigning the public namespace.
    // Wait until that script's synchronous initialization has finished.
    window.predocMapReady = () => queueMicrotask(() => {
      if (window.naver?.maps.Map === undefined) { fail(); return; }
      window.clearTimeout(timer);
      delete window.predocMapReady;
      resolve(window.naver.maps);
    });
    script.src = `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${encodeURIComponent(clientId)}&callback=predocMapReady`;
    script.async = true;
    // Naver authenticates the registered origin. Never send the path or query.
    script.referrerPolicy = "origin";
    script.onerror = fail;
    document.head.appendChild(script);
  });
  pending = { clientId, promise };
  return promise;
}

export function createFacilityMap(element: HTMLElement, sdk: NaverSdk, onSelect: (id: string) => void, onReady: () => void, onError: () => void) {
  const map = new sdk.Map(element, { center: new sdk.LatLng(36.5, 127.4), zoom: 9, minZoom: 6, maxZoom: 21, scrollWheel: true, pinchZoom: true, draggable: true, keyboardShortcuts: false });
  const tileTimer = window.setTimeout(onError, 12_000);
  const tileListener = sdk.Event.addListener(map, "tilesloaded", () => { window.clearTimeout(tileTimer); onReady(); });
  let markers: Marker[] = [];
  let buttons: Array<{ id: string; element: HTMLButtonElement }> = [];
  let ownPosition: UserMapPosition | null = null;
  let accuracyCircle: (Marker & { getBounds(): Bounds }) | null = null;
  function clear(): void {
    for (const marker of markers) marker.setMap(null);
    markers = []; buttons = [];
    accuracyCircle?.setMap(null); accuracyCircle = null; ownPosition = null;
  }
  return {
    update(points: MapPoint[], userPosition: UserMapPosition | null = null): void {
      clear();
      const bounds = new sdk.LatLngBounds();
      if (userPosition !== null) {
        ownPosition = userPosition;
        const center = new sdk.LatLng(userPosition.latitude, userPosition.longitude);
        const pin = document.createElement("span");
        pin.className = "map-user-position";
        pin.setAttribute("role", "img");
        pin.setAttribute("aria-label", userPositionLabel(userPosition));
        const label = document.createElement("span"); label.textContent = userPositionLabel(userPosition);
        pin.appendChild(label);
        markers.push(new sdk.Marker({ position: center, map, zIndex: 1000, icon: { content: pin, anchor: new sdk.Point(12, 12) } }));
        bounds.extend(center);
        if (userPosition.accuracyMeters > 0) {
          accuracyCircle = new sdk.Circle({ map, center, radius: userPosition.accuracyMeters, fillColor: "#2563eb", fillOpacity: 0.09, strokeColor: "#2563eb", strokeOpacity: 0.4, strokeWeight: 1, clickable: false });
          bounds.extend(accuracyCircle.getBounds().getSW());
          bounds.extend(accuracyCircle.getBounds().getNE());
        }
      }
      points.forEach((point) => {
        const position = new sdk.LatLng(point.latitude, point.longitude);
        bounds.extend(position);
        const button = document.createElement("button");
        button.type = "button";
        button.className = "map-marker";
        button.textContent = String(point.listNumber);
        button.setAttribute("aria-label", `${point.listNumber}. ${point.name} 목록 보기`);
        button.setAttribute("aria-pressed", "false");
        button.addEventListener("click", () => onSelect(point.id));
        markers.push(new sdk.Marker({ position, map, icon: { content: button, anchor: new sdk.Point(24, 48) } }));
        buttons.push({ id: point.id, element: button });
      });
      if (userPosition !== null && points.length === 0 && userPosition.accuracyMeters <= 1000) { map.setCenter(new sdk.LatLng(userPosition.latitude, userPosition.longitude)); map.setZoom(15); }
      else if (userPosition !== null) map.fitBounds(bounds, 48);
      else if (points.length === 1) { map.setCenter(new sdk.LatLng(points[0]!.latitude, points[0]!.longitude)); map.setZoom(15); }
      else if (points.length > 1) map.fitBounds(bounds, 48);
    },
    centerOnUser(): void {
      if (ownPosition === null) return;
      if (ownPosition.accuracyMeters > 1000 && accuracyCircle !== null) map.fitBounds(accuracyCircle.getBounds(), 48);
      else { map.setCenter(new sdk.LatLng(ownPosition.latitude, ownPosition.longitude)); map.setZoom(15); }
    },
    select(id: string | null): void {
      for (const button of buttons) button.element.setAttribute("aria-pressed", String(button.id === id));
    },
    zoomBy(delta: number): void { map.setZoom(Math.max(6, Math.min(21, map.getZoom() + delta))); },
    resize(): void {
      const center = map.getCenter();
      map.setSize({ width: element.clientWidth, height: element.clientHeight });
      map.setCenter(center);
    },
    destroy(): void { window.clearTimeout(tileTimer); sdk.Event.removeListener(tileListener); clear(); map.destroy(); },
  };
}
